import { type CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
// The same metadata `libphonenumber-js` itself loads by default, so this adds nothing to the bundle.
import metadata from "libphonenumber-js/min/metadata";
import { getCountryName } from "./country-names";

/**
 * What a {@link PhoneInput} holds: the selected country and the number as the
 * user typed it (local form, e.g. `0412 345 678`). Kept as typed rather than
 * normalised so editing never rewrites text under the cursor — use
 * {@link phoneInputValueToE164} to get the canonical number.
 */
export interface PhoneInputValue {
  readonly country: CountryCode;
  readonly nationalNumber: string;
}

export interface CountryOption {
  readonly country: CountryCode;
  readonly name: string;
  /** Without the leading `+`, e.g. `"61"`. */
  readonly callingCode: string;
}

/** The E.164 number (`+61412345678`), or `undefined` when empty or not a valid number for the country. */
export function phoneInputValueToE164(value: PhoneInputValue): string | undefined {
  const parsed = parsePhoneNumberFromString(value.nationalNumber.trim(), value.country);
  return parsed?.isValid() ? parsed.number : undefined;
}

/**
 * Splits a stored E.164 number back into country and national number, in the
 * country's local format (`0412 345 678`). A stored number that isn't valid
 * (saved before anything checked per-country rules) is split by its calling
 * code instead — `+61123456789` becomes 🇦🇺 `123456789`, not 🇦🇺 `+61123456789`
 * with the code shown twice. One with no known calling code is kept verbatim,
 * so the user sees (and can fix) what's on file rather than an empty field.
 */
export function phoneInputValueFromE164(e164: string | null, fallbackCountry: CountryCode): PhoneInputValue {
  if (e164 === null || e164 === "") return { country: fallbackCountry, nationalNumber: "" };
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed === undefined) return { country: fallbackCountry, nationalNumber: e164 };
  if (parsed.country !== undefined) return { country: parsed.country, nationalNumber: parsed.formatNational() };
  const country = mainCountryForCallingCode(parsed.countryCallingCode);
  if (country === undefined) return { country: fallbackCountry, nationalNumber: e164 };
  return { country, nationalNumber: parsed.nationalNumber };
}

/** The main country for a shared calling code — `1` is the US, not Antigua (first alphabetically). */
function mainCountryForCallingCode(callingCode: string): CountryCode | undefined {
  // Each calling code lists its countries main-first.
  return metadata.country_calling_codes[callingCode]?.[0];
}

/**
 * The next value after the user types or pastes `text`. A full international
 * number (`+64 21 …`) moves the country selector to match, so pasting from a
 * contact card just works; anything else is kept as typed (and still converts
 * via {@link phoneInputValueToE164}, which reads a leading `+` itself).
 */
export function withTypedText(value: PhoneInputValue, text: string): PhoneInputValue {
  if (text.trim().startsWith("+")) {
    // Only once complete: splitting a half-typed `+614…` would yank the text
    // out from under someone typing the international form digit by digit.
    const parsed = parsePhoneNumberFromString(text);
    if (parsed?.country !== undefined && parsed.isValid()) {
      return { country: parsed.country, nationalNumber: parsed.formatNational() };
    }
  }
  return { country: value.country, nationalNumber: text };
}

/** Every country with dialling metadata: `pinned` first in the given order, then the rest by name. */
export function getCountryOptions(pinned: readonly CountryCode[]): readonly CountryOption[] {
  const toOption = (country: CountryCode): CountryOption => ({
    country,
    name: getCountryName(country),
    callingCode: getCountryCallingCode(country),
  });
  const pinnedSet = new Set(pinned);
  const rest = getCountries()
    .filter((c) => !pinnedSet.has(c))
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...pinned.map(toOption), ...rest];
}

/** Matches on name, ISO code, or calling code (with or without `+`). */
export function filterCountryOptions(options: readonly CountryOption[], query: string): readonly CountryOption[] {
  const q = query.trim().toLowerCase();
  if (q === "") return options;
  const digits = q.replace(/^\+/, "");
  const isDialQuery = /^\d+$/.test(digits);
  return options.filter((o) =>
    isDialQuery ? o.callingCode.startsWith(digits) : o.name.toLowerCase().includes(q) || o.country.toLowerCase() === q,
  );
}

const REGIONAL_INDICATOR_A = 0x1f1e6;

/** The flag emoji for an ISO 3166 country code. Platforms without flag glyphs show the two letters instead. */
export function getFlagEmoji(country: CountryCode): string {
  return String.fromCodePoint(
    REGIONAL_INDICATOR_A + country.charCodeAt(0) - 65,
    REGIONAL_INDICATOR_A + country.charCodeAt(1) - 65,
  );
}
